import React from "react";
import StatBadges from "./stat_badge";
import { ReaderItem, StatBadge } from "../../../models/reader";
import { useHistory } from "react-router";
import { getAuthCookie } from "../../../middleware/cookie-manager";
import { renderBuyText } from "../../../middleware/common-functions";
import { AuthorReaderItem } from "../../../models/authors";
import { RatingStars } from "../../common/icons";
import { CopyButton } from "../../common/buttons";

interface HeroSectionProps {
  book: ReaderItem | AuthorReaderItem;
  triggerClick: (ev: any) => void;
  showCover?: boolean;
  is_author?: boolean;
  is_teacher: boolean;
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  book,
  triggerClick,
  showCover = false,
  is_author = false,
  is_teacher,
}) => {
  const stats: StatBadge[] = [
    { label: "Level", value: book.level.name.toUpperCase() },
    { label: "Unique words", value: String(book.unique_words) },
    { label: "Total words", value: String(book.total_words) },
  ];

  return (
    <section style={styles.section} aria-label="Book details">
      <div className="row">
        <div className="col-md-5">
          <StatBadges stats={stats} />

          {book.coming_soon ? (
            <div className="alert alert-info">Coming Soon</div>
          ) : (
            <ActionButtons
              triggerClick={triggerClick}
              reader={book}
              is_author={is_author}
            />
          )}
          {showCover && book.images.cover ? (
            <div style={styles.coverWrapper}>
              <img
                style={styles.coverImg}
                src={`${book.images.cover}`}
                alt={book.title}
              />
            </div>
          ) : null}
        </div>
        {/* Right: metadata */}
        <div className="col-md-7">
          <div className="row">
            <div className="col d-flex align-items-center">
              <h1 style={styles.title}>{book.title}</h1>
            </div>
            <div className="col d-flex mb-2">
              {is_teacher ? (
                <div className="rating_group">
                  <RatingStars
                    gold={false}
                    id="teacher_rating"
                    value={book.rating_teachers}
                  />
                  Teachers' Rating{" "}
                </div>
              ) : null}
              {is_teacher ? (
                <div className="rating_group teachers">
                  <RatingStars
                    gold={false}
                    id="students_rating"
                    value={book.rating_students}
                  />
                  My Students'{" "}
                </div>
              ) : null}
              <div className="rating_group teachers">
                <RatingStars
                  gold={false}
                  id="students_rating"
                  value={book.rating}
                />
                Global
              </div>
            </div>
          </div>
          <div style={styles.tags}>
            {book.author && book.author.name ? (
              <span style={{ ...styles.tag, ...styles.tagHighlight }}>
                {book.author.name}
              </span>
            ) : null}

            <span style={styles.tag}>
              {book.chapters} Chapter{book.chapters !== 1 ? "s" : ""}
            </span>
            <span style={{ ...styles.tag }}>{book.language.name}</span>
          </div>
          {book.description_short && (
            <div
              className="book_desc"
              dangerouslySetInnerHTML={{ __html: book.description_short }}
            ></div>
          )}
        </div>
      </div>
    </section>
  );
};
interface ActionButtonsProps {
  triggerClick: (ev: any) => void;
  reader: ReaderItem | AuthorReaderItem;
  is_author?: boolean;
}

export const ActionButtons: React.FC<ActionButtonsProps> = ({
  triggerClick,
  reader,
  is_author,
}) => {
  const history = useHistory();
  const user = getAuthCookie();
  const book_url = is_author ? `creator_reader` : `reader`;
  const book_info_url = is_author ? `creatorbook` : `bookdetails`;
  const goToBook = (continueReading = false) => {
    let bookUrl = `/${book_url}/${reader.url}${is_author ? "" : `/${reader.id}`}${
      reader.has_prereading ? "/pre-reading" : ""
    }`;
    if (continueReading && reader.user_chapter) {
      bookUrl += `/${reader.user_chapter}`;
    }
    history.push(bookUrl);
  };
  const onViewMore = () => {
    history.push(
      `/${book_info_url}/${reader.url}/${is_author ? "" : reader.id}`,
    );
  };
  return (
    <div style={styles.row}>
      <button
        style={styles.primary}
        onClick={() => {
          goToBook();
        }}
      >
        <span style={styles.icon}>▶</span>
        {reader.user_chapter ? "Continue " : "Start "} Reading
      </button>
      {reader.user_chapter ? (
        <button
          style={styles.primary}
          onClick={() => {
            goToBook(false);
          }}
        >
          <span style={styles.icon}>↩</span>
          <span className="xs_hidden">Start from</span> the Beginning
        </button>
      ) : null}
      <button
        style={styles.secondary}
        className="text-white"
        onClick={onViewMore}
      >
        <span style={styles.icon}>≡</span>
        {user?.is_teacher ? "View Student Data" : "View Details"}
      </button>
      {reader.vocab_url ? (
        <a
          href={`${process.env.REACT_APP_SITE_URL}${reader.vocab_url}`}
          download="true"
          target="_blank"
          onClick={triggerClick}
          data-action="DownloadVocabClick"
          rel="noreferrer"
          style={styles.accent}
          className="text-white"
        >
          <span style={styles.icon}>↓</span>
          Words to Know (download)
        </a>
      ) : null}
      {user?.is_teacher ? (
        <CopyButton
          style={styles.accent}
          p="px-1"
          iconHidden={true}
          text={`https://flangoo.com/${book_info_url}/${reader.url}/${reader.id}`}
        >
          <i className="ml-2 fas fa-external-link"></i>
          Copy Link to This Book
        </CopyButton>
      ) : null}
      {user?.is_teacher && reader.print_url ? (
        <a
          href={`${reader.print_url}`}
          target="_blank"
          onClick={triggerClick}
          data-action="DownloadVocabClick"
          rel="noreferrer"
          style={styles.accent}
          className="text-white"
        >
          <span style={styles.icon}>🛒</span>
          {renderBuyText(reader.categories)}
        </a>
      ) : null}
    </div>
  );
};
const styles: Record<string, React.CSSProperties> = {
  section: {
    padding: "20px 24px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 2fr",
    gap: 0,
    alignItems: "start",
  },
  coverWrapper: {
    maxWidth: 500,
    margin: "20px auto",
    textAlign: "center",
  },
  coverImg: {
    height: "70vh",
  },
  meta: {
    paddingLeft: 20,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  title: {
    fontSize: 22,
    fontWeight: 600,
    margin: 0,
  },
  tags: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginBottom: 5,
  },
  tag: {
    background: "rgba(255,255,255,0.08)",
    border: "0.5px solid rgba(255,255,255,0.12)",
    // color: "#c2c0b6",
    fontSize: 11,
    padding: "3px 9px",
    borderRadius: 20,
  },
  tagHighlight: {
    background: "rgba(99,102,241,0.15)",
    borderColor: "rgba(99,102,241,0.3)",
    color: "#a5b4fc",
  },
  descStrong: {
    // color: "#c2c0b6",
    fontWeight: 500,
  },
  descAuthor: {
    // color: "#6b6a64",
    fontSize: 12,
    fontStyle: "italic",
  },
  row: {
    display: "grid",
    marginTop: 15,
    gridTemplateColumns: "1fr 1fr",
    paddingRight: 30,
    gap: 8,
  },
  primary: {
    background: "#e8393a",
    border: "none",
    color: "#101010",
    fontSize: 13,
    fontWeight: 600,
    padding: "9px 20px",
    borderRadius: 8,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
    transition: "background 0.15s",
    whiteSpace: "nowrap",
  },
  secondary: {
    background: "rgba(255,255,255,0.06)",
    border: "0.5px solid rgba(255,255,255,0.14)",
    // color: "#c2c0b6",
    fontSize: 13,
    padding: "8px 16px",
    borderRadius: 8,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 5,
    transition: "background 0.15s, color 0.15s",
    whiteSpace: "nowrap",
  },
  accent: {
    background: "rgba(59,130,246,0.15)",
    border: "0.5px solid rgba(59,130,246,0.3)",
    // color: "#93c5fd",
    fontSize: 13,
    padding: "8px 16px",
    borderRadius: 8,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 5,
    transition: "background 0.15s",
    whiteSpace: "nowrap",
  },
  icon: {
    fontSize: 12,
    lineHeight: 1,
  },
};

export default HeroSection;
